import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Lock, Loader2, ShieldCheck, KeyRound, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const getToken = () =>
  sessionStorage.getItem('session_token') || localStorage.getItem('session_token') || '';

// Non-dismissible daily gate. Every 24h a non-admin user re-enters their panel
// password. Users who have NOT set a panel password yet are forced to CREATE
// one here before they can use the app. Admins never see it.
export function DailyReverifyGate() {
  const { currentUser, reverifyRequired, setReverifyRequired, api } = useApp();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [mustCreate, setMustCreate] = useState(false); // true when user has no password yet
  const [hasHiteam, setHasHiteam] = useState(false);
  const [method, setMethod] = useState(() => localStorage.getItem('reverify_method') || 'panel');
  const useHiteam = method === 'hiteam' && hasHiteam && !mustCreate;

  const isAdmin = (currentUser?.role || '').trim().toLowerCase() === 'admin';

  // Poll status every 5 min so a long-open session gets gated after 24h, and
  // learn whether the user needs to CREATE vs VERIFY their password.
  const checkStatus = useCallback(async () => {
    const token = getToken();
    if (!token || isAdmin) return;
    try {
      const res = await api.reverifyStatus(token);
      if (res.data?.reverify_required) setReverifyRequired(true);
      setMustCreate(res.data?.has_password === false);
      setHasHiteam(!!res.data?.has_hiteam_password);
    } catch { /* ignore transient errors */ }
  }, [api, isAdmin, setReverifyRequired]);

  useEffect(() => {
    if (!currentUser || isAdmin) return;
    checkStatus();
    const t = setInterval(checkStatus, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [currentUser, isAdmin, checkStatus]);

  if (!currentUser || isAdmin || !reverifyRequired) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!password) { toast.error('Please enter a password'); return; }
    if (mustCreate) {
      if (password.length < 4) { toast.error('Password must be at least 4 characters'); return; }
      if (password !== confirm) { toast.error('Passwords do not match'); return; }
    }
    setBusy(true);
    try {
      if (mustCreate) {
        await api.reverifySetPassword(getToken(), password);
        toast.success('Panel password created — use it to log in from next time.');
      } else {
        await api.reverifyPassword(getToken(), password, useHiteam ? 'hiteam' : 'panel');
        localStorage.setItem('reverify_method', useHiteam ? 'hiteam' : 'panel');
        toast.success('Verified — you’re all set for the day.');
      }
      setPassword(''); setConfirm('');
      setReverifyRequired(false);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 401) toast.error(useHiteam ? 'Incorrect HiTeam password' : 'Incorrect password');
      else toast.error((typeof detail === 'string' && detail) || 'Failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="daily-reverify-overlay"
      className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        data-testid="daily-reverify-modal"
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
      >
        <div className="h-1.5 w-full bg-gradient-to-r from-amber-500 to-emerald-500" />
        <div className="p-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            {mustCreate ? <KeyRound className="h-7 w-7" /> : <ShieldCheck className="h-7 w-7" />}
          </div>
          <h2 className="text-lg font-bold text-slate-900" data-testid="daily-reverify-title">
            {mustCreate ? 'Create your panel password' : 'Confirm it’s you'}
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            {mustCreate
              ? 'To keep your account secure, please set a panel password. You’ll use it to log in and to unlock the app each day.'
              : useHiteam
                ? 'For your security, please enter your HiTeam portal password to continue. We’ll only ask once a day.'
                : 'For your security, please re-enter your panel password to continue. We’ll only ask once a day.'}
          </p>
          {!mustCreate && hasHiteam && (
            <div className="mt-4 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 text-xs font-semibold" data-testid="daily-reverify-method">
              {[['panel', 'Panel password', Lock], ['hiteam', 'HiTeam password', Building2]].map(([m, lbl, Icon]) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`daily-reverify-method-${m}`}
                  onClick={() => { setMethod(m); setPassword(''); }}
                  className={`flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 transition-colors ${(useHiteam ? 'hiteam' : 'panel') === m ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {lbl}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={submit} className="mt-4 space-y-3 text-left">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="daily-reverify-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mustCreate ? 'Choose a panel password (min 4 chars)' : useHiteam ? 'Enter your HiTeam portal password' : 'Enter your panel password'}
                className="pl-10"
                autoFocus
                required
              />
            </div>
            {mustCreate && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="daily-reverify-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm password"
                  className="pl-10"
                  required
                />
              </div>
            )}
            <Button
              type="submit"
              data-testid="daily-reverify-submit"
              disabled={busy}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 gap-2"
            >
              {busy
                ? <><Loader2 className="h-4 w-4 animate-spin" /> {mustCreate ? 'Saving...' : 'Verifying...'}</>
                : mustCreate
                  ? <><KeyRound className="h-4 w-4" /> Create Password</>
                  : <><ShieldCheck className="h-4 w-4" /> Unlock</>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
